import { useEffect, useMemo } from 'react';
import type { ReactElement, ReactNode } from 'react';
import { identify } from '@cbhq/client-analytics';

import type { UseFetchUserProfileResult } from '../hooks/useFetchUserProfile';
import { useFetchUserProfile } from '../hooks/useFetchUserProfile';
import { useIdentifySession } from '../providers/SessionProvider';
import { rumSetUser } from '../utils/analytics/datadogRum';
import { setErrorVitalsUser } from '../utils/errorVitals';

import { useRegisterInitialLoader } from './InitialLoaderProvider';
import type { UserContextType } from './UserProfileContext';
import { UserProfileContext } from './UserProfileContext';

type UserProfileProviderProps = {
  children: ReactNode;
  user?: UseFetchUserProfileResult;
};

export function UserProfileProvider({ children, user }: UserProfileProviderProps): ReactElement {
  const data = useFetchUserProfile();
  const userData = user ?? data;
  const userId = userData?.userProfile?.id ?? '';
  const { error, getUserProfile, loading, userProfile } = userData;

  useRegisterInitialLoader(loading, 'user-profile-provider');

  const value = useMemo<UserContextType>(
    () => ({
      error,
      getUserProfile,
      loading,
      userProfile,
    }),
    [error, getUserProfile, loading, userProfile],
  );

  useIdentifySession(userData?.userProfile);

  useEffect(() => {
    setErrorVitalsUser(userId);
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      return;
    }

    rumSetUser(userId);
    identify({ userId });
  }, [userId]);

  // Keep the subtree mounted while the global initial loader is visible so
  // downstream providers can start fetching in parallel.
  return <UserProfileContext.Provider value={value}>{children}</UserProfileContext.Provider>;
}
